import type { Genome } from '@/domain/genetics/types';
import type { BiomeId } from '@/domain/world/types';
import type { EnemyArchetype } from './types';
import type { EnemyAttackPattern, EnemyRank } from './enemy-behavior';

export interface WildCombatProfile {
  archetype: EnemyArchetype;
  maxHp: number;
  damage: number;
  speed: number;
  attackCooldown: number;
  scale: number;
  patterns: EnemyAttackPattern[];
}

const BASE: Record<EnemyArchetype, Pick<WildCombatProfile, 'maxHp' | 'damage' | 'speed' | 'attackCooldown'>> = {
  fast: { maxHp: 28, damage: 8, speed: 115, attackCooldown: 900 },
  tank: { maxHp: 75, damage: 17, speed: 55, attackCooldown: 1_700 },
  ranged: { maxHp: 24, damage: 8, speed: 85, attackCooldown: 1_200 },
};

const PRIMARY_PATTERN: Record<EnemyArchetype, EnemyAttackPattern> = {
  fast: 'lunge', tank: 'slam', ranged: 'shot',
};

const PATTERN_ORDER: readonly EnemyAttackPattern[] = ['lunge', 'slam', 'shot'];

export function deriveWildCombatProfile(genome: Genome, biome: BiomeId, rank: EnemyRank): WildCombatProfile {
  const archetype = archetypeFor(genome);
  const base = BASE[archetype];
  let hp = base.maxHp;
  let damage = base.damage;
  let speed = base.speed;

  if (genome.body === 'round') { hp += 10; speed -= 5; }
  else if (genome.body === 'tall') damage += 2;
  else if (genome.body === 'ghost') { hp -= 5; speed += 15; }
  else if (genome.body === 'blob') { hp += 15; speed -= 10; }
  else if (genome.body === 'fish') speed += 10;
  if (biome === 'coast' && genome.body === 'fish') speed += 10;

  const predisposition = clamp(genome.bodyPredisposition, 0, 100);
  hp *= 0.85 + predisposition * 0.003;
  let scale = 0.9 + predisposition * 0.002;
  if (rank === 'dominant') {
    hp *= 1.8;
    damage *= 1.25;
    scale *= 1.2;
  }

  const primary = PRIMARY_PATTERN[archetype];
  const patterns = rank === 'dominant'
    ? [primary, PATTERN_ORDER.find((pattern) => pattern !== primary)!]
    : [primary];

  return {
    archetype,
    maxHp: Math.round(clamp(hp, 14, 180)),
    damage: Math.round(clamp(damage, 4, 30)),
    speed: Math.round(clamp(speed, 40, 160)),
    attackCooldown: Math.round(clamp(base.attackCooldown, 600, 2_400)),
    scale: Number(clamp(scale, 0.75, 1.35).toFixed(3)),
    patterns,
  };
}

function archetypeFor(genome: Genome): EnemyArchetype {
  if (genome.ears === 'antenna') return 'ranged';
  if (genome.horns !== 'none') return 'fast';
  if ((genome.body === 'round' || genome.body === 'blob') && genome.bodyPredisposition >= 65) return 'tank';
  if (genome.body === 'round' || genome.body === 'blob') return 'tank';
  if (genome.body === 'tall') return 'ranged';
  return 'fast';
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
